import Icon, { IconVariant } from "@galaxy-io/dls/icons/Icon";
import Field from "@galaxy-io/dls/inputs/Field";
import FileInput from "@galaxy-io/dls/inputs/FileInput";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Text, { TextSize } from "@galaxy-io/dls/text/Text";
import type { ImportFile, ImportKind } from "@gtm/contracts/imports";
import { FileCsvIcon } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Input } from "@/components/ui/input";
import { FeedViewport } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { RelativeTime } from "@/components/workspace/relative-time";
import { Status } from "@/components/workspace/status";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { nouns } from "./import-fields";
import { DropArea, FileCell } from "./import-layout";

export function importStatus(file: ImportFile) {
  return file.completed >= file.total ? (
    <Status value="completed" label="Complete" />
  ) : file.completed ? (
    <Status value="partial" label={`${file.completed}/${file.total}`} />
  ) : (
    <Status value="waiting" label="Not started" />
  );
}

export function UploadStep({
  kind,
  uploading,
  history,
  historyError,
  notice,
  onRetry,
  onFile,
  onOpen,
}: {
  kind: ImportKind;
  uploading: string | null;
  history: ImportFile[] | undefined;
  historyError?: Error;
  notice?: ReactNode;
  onRetry: () => void;
  onFile: (file: File) => void;
  onOpen: (file: ImportFile) => void;
}) {
  const [fileError, setFileError] = useState("");
  const [q, setQ] = useState("");
  const all = (history ?? []).filter((f) => f.kind === kind);
  const recent = all.filter((f) => f.filename.toLowerCase().includes(q.trim().toLowerCase()));
  // With nothing to resume, the drop target is the whole dialog.
  const hasHistory = !history || !!all.length;
  return (
    <>
      <DropArea $grow={!hasHistory}>
        {notice}
        <Field
          label="CSV file"
          description={`One row per ${nouns[kind][0]} · Up to 10 MB · Columns are matched automatically`}
        >
          <FileInput
            accept=".csv,text/csv"
            value={[]}
            fillWidth
            isDisabled={!!uploading}
            placeholder={uploading ? `Uploading ${uploading}…` : "Drop a CSV file here"}
            error={fileError || undefined}
            onReject={() => setFileError("Choose a CSV file (.csv).")}
            onChange={(files) => {
              const file = files[0];
              if (!file) return;
              if (file.size > 10 * 1024 * 1024) {
                setFileError("Choose a CSV file smaller than 10 MB.");
                return;
              }
              setFileError("");
              onFile(file);
            }}
          />
        </Field>
      </DropArea>
      {hasHistory && (
        <FeedViewport>
          <BaseToolbar
            leadingActions={
              <Input
                aria-label="Search recent imports"
                placeholder="Search recent imports…"
                value={q}
                onChange={(e) => setQ(e)}
                type="search"
                size={InputSize.SMALL}
                label="Search recent imports"
                hideLabel
              />
            }
          />
          <RecordTable
            label="Recent imports"
            isLoading={!history && !historyError}
            error={historyError}
            onRetry={onRetry}
            rowKeys={recent.map((f) => f.id)}
            onRowClick={(id) => {
              const file = recent.find((f) => f.id === id);
              if (file && !uploading) onOpen(file);
            }}
            columns={[
              { key: "file", label: "File", width: "auto", minWidth: 220 },
              { key: "rows", label: "Rows", width: 80, align: "right" as const },
              { key: "status", label: "Status", width: 130 },
              { key: "uploader", label: "Uploaded by", width: 150 },
              { key: "uploaded", label: "Uploaded", width: 130 },
            ]}
            rows={recent.map((f) => ({
              file: (
                <FileCell>
                  <Icon component={FileCsvIcon} size={16} variant={IconVariant.SECONDARY} />
                  <Text size={TextSize.BODY_SM} lineClamp={1}>
                    {f.filename}
                  </Text>
                </FileCell>
              ),
              rows: f.total.toLocaleString(),
              status: importStatus(f),
              uploader: f.uploader || "—",
              uploaded: <RelativeTime value={f.createdAt} label="Uploaded" />,
            }))}
          />
        </FeedViewport>
      )}
    </>
  );
}
